import { useState } from "react";
import { ShoppingCart, Minus, Plus, Check } from "lucide-react";
import { useCart } from "@/lib/cart/cart-store";
import { showAddedToCartToast } from "@/components/cart/AddedToCartToast";
import { reportClientError, trackCart } from "@/lib/client-errors/tracker";

type Props = {
  productId: string;
  variantId: string | null;
  sku: string;
  slug: string;
  title: string;
  brand?: string | null;
  variantLabel?: string | null;
  image?: string | null;
  unitPriceCents: number;
  currency?: string;
  maxQty?: number | null;
  disabled?: boolean;
  disabledLabel?: string;
  /** Notified whenever the shopper changes the quantity stepper. */
  onQtyChange?: (qty: number) => void;
};

export function AddToCartButton(props: Props) {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const add = useCart((s) => s.add);
  const open = useCart((s) => s.open);
  

  const max = props.maxQty ?? 99;

  const handleAdd = () => {
    try {
      trackCart("add", {
        product_id: props.productId,
        variant_id: props.variantId,
        sku: props.sku,
        qty,
        unit_price_cents: props.unitPriceCents,
      });
      add({
        productId: props.productId,
        variantId: props.variantId,
        sku: props.sku,
        slug: props.slug,
        title: props.title,
        brand: props.brand,
        variantLabel: props.variantLabel,
        image: props.image,
        unitPriceCents: props.unitPriceCents,
        currency: props.currency ?? "EUR",
        maxQty: props.maxQty ?? null,
        qty,
      });
      setAdded(true);
      setTimeout(() => setAdded(false), 1400);
      showAddedToCartToast({
        message: "Added to cart",
        description: `${qty}× ${props.title}${props.variantLabel ? ` · ${props.variantLabel}` : ""}`,
      });
      // Slight delay so the toast is visible before the drawer opens.
      setTimeout(() => open(), 250);
    } catch (err) {
      reportClientError(err, {
        source: "cart",
        action: "AddToCartButton.handleAdd",
        context: { product_id: props.productId, variant_id: props.variantId, qty },
      });
    }
  };

  if (props.disabled) {
    return (
      <button
        disabled
        className="w-full py-3 bg-slate-200 text-slate-500 rounded-md font-semibold cursor-not-allowed"
      >
        {props.disabledLabel ?? "Currently unavailable"}
      </button>
    );
  }

  return (
    <div className="flex gap-2">
      <div className="inline-flex items-center rounded-md border bg-white">
        <button
          type="button"
          aria-label="Decrease quantity"
          onClick={() =>
            setQty((q) => {
              const next = Math.max(1, q - 1);
              props.onQtyChange?.(next);
              return next;
            })
          }
          className="grid h-11 w-10 place-items-center hover:bg-slate-50"
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="min-w-8 text-center text-sm font-bold tabular-nums text-[#172554]">
          {qty}
        </span>
        <button
          type="button"
          aria-label="Increase quantity"
          onClick={() =>
            setQty((q) => {
              const next = Math.min(max, q + 1);
              props.onQtyChange?.(next);
              return next;
            })
          }
          className="grid h-11 w-10 place-items-center hover:bg-slate-50"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <button
        type="button"
        onClick={handleAdd}
        className="flex-1 inline-flex items-center justify-center gap-2 py-3 bg-[#172554] text-white rounded-md font-semibold hover:bg-royal transition"
      >
        {added ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
        {added ? "Added!" : "Add to cart"}
      </button>
    </div>
  );
}